import { initializeCurrency } from '../actions/runGetCurrency';

/**
 * Global currency manager for the application
 */
class CurrencyManager {
    constructor() {
        this.isInitialized = false;
        this.eventListeners = new Map();
    }

    /**
     * Initialize the currency system globally
     */
    async initialize(options = {}) {
        if (this.isInitialized) {
            return;
        }

        const {
            defaultCountry = 'AU',
            defaultCurrency = 'AUD',
            defaultSymbol = '$',
            autoInit = true
        } = options;

        try {
            if (autoInit) {
                await initializeCurrency(defaultCountry, defaultCurrency, defaultSymbol);
            }
            
            this.isInitialized = true;
            
            // Emit initialization complete event
            this.emit('manager:initialized', {
                defaultCountry,
                defaultCurrency,
                defaultSymbol
            });
            
        } catch (error) {
            console.error('Currency manager initialization failed:', error);
            this.emit('manager:error', { error: error.message });
        }
    }

    /**
     * Add event listener
     */
    on(eventName, callback) {
        if (!this.eventListeners.has(eventName)) {
            this.eventListeners.set(eventName, new Set());
        }
        this.eventListeners.get(eventName).add(callback);
    }

    /**
     * Remove event listener
     */
    off(eventName, callback) {
        if (this.eventListeners.has(eventName)) {
            this.eventListeners.get(eventName).delete(callback);
        }
    }

    /**
     * Emit event to all listeners
     */
    emit(eventName, data) {
        if (this.eventListeners.has(eventName)) {
            this.eventListeners.get(eventName).forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error(`Error in currency manager event listener for ${eventName}:`, error);
                }
            });
        }
    }

    /**
     * Get initialization status
     */
    getInitializationStatus() {
        return this.isInitialized;
    }

    /**
     * Reset the manager (useful for testing)
     */
    reset() {
        this.isInitialized = false;
        this.eventListeners.clear();
    }
}

// Create singleton instance
const currencyManager = new CurrencyManager();

// Auto-initialize when DOM is ready
if (typeof window !== 'undefined') {
    const initWhenReady = () => {
        // Get initial values from Shopify global or defaults
        const defaultCountry = window.Shopify?.country || 'AU';
        const defaultCurrency = window.Shopify?.currency?.active || 'AUD';
        const defaultSymbol = '$';

        currencyManager.initialize({
            defaultCountry,
            defaultCurrency,
            defaultSymbol,
            autoInit: true
        });
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initWhenReady);
    } else {
        initWhenReady();
    }
}

export default currencyManager;
